import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { AlertTriangle, Check, LoaderCircle, X } from "lucide-react";
import { ApiError } from "../lib/api";

export function Button({
  variant = "secondary",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  return (
    <button className={`button button--${variant} ${className}`.trim()} {...props}>
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Tag({
  tone = "neutral",
  children,
}: PropsWithChildren<{ tone?: "neutral" | "accent" | "outline" | "dark" | "vip" }>) {
  return <span className={`tag tag--${tone}`}>{children}</span>;
}

export function Loading({ label = "载入中" }: { label?: string }) {
  return (
    <div className="state state--loading" role="status">
      <LoaderCircle size={18} className="spin" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : "暂时无法载入内容";
  return (
    <div className="state state--error" role="alert">
      <AlertTriangle size={20} />
      <div>
        <strong>没有完成请求</strong>
        <p>{message}</p>
      </div>
      {retry ? <Button onClick={retry}>重新加载</Button> : null}
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="eyebrow">EMPTY · 000</span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Dialog({
  open,
  title,
  eyebrow,
  onClose,
  children,
  wide = false,
  reader = false,
}: PropsWithChildren<{
  open: boolean;
  title: string;
  eyebrow?: string;
  onClose: () => void;
  wide?: boolean;
  reader?: boolean;
}>) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", handler);
    };
  }, [onClose, open]);
  if (!open) return null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`dialog ${reader ? "dialog--reader" : wide ? "dialog--wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="dialog__header">
          <div>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭">
            <X size={18} />
          </button>
        </header>
        <div className="dialog__body">{children}</div>
      </section>
    </div>
  );
}

interface ToastItem {
  id: string;
  message: string;
  tone: "success" | "error";
}

interface ToastContextValue {
  push(message: string, tone?: ToastItem["tone"]): void;
  fromError(error: unknown): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: PropsWithChildren) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: ToastItem["tone"] = "success") => {
    const id = crypto.randomUUID();
    setItems((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setItems((current) => current.filter((item) => item.id !== id));
    }, 3600);
  }, []);
  const fromError = useCallback(
    (error: unknown) => {
      push(error instanceof ApiError || error instanceof Error ? error.message : "操作没有完成", "error");
    },
    [push],
  );
  return (
    <ToastContext.Provider value={{ push, fromError }}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((item) => (
          <div className={`toast toast--${item.tone}`} key={item.id}>
            {item.tone === "success" ? <Check size={16} /> : <AlertTriangle size={16} />}
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return (
    <button
      className={`toggle ${checked ? "toggle--on" : ""}`}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle__track"><span className="toggle__thumb" /></span>
      <span>{label}</span>
    </button>
  );
}
